import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography
} from "@mui/material"
import React, { useEffect, useState } from "react"
import { useGeolocated } from "react-geolocated"
import { useTranslation } from "react-i18next"
import { useProfile } from "../../../context/ProfileContext"
import { canParticipate, displayPriceToFixed, displayPriceToNumber, isPrivilegedUser } from "../../../utils/helpers/LocalPrice"
import { Fuel } from "../../../utils/model/Fuel"
import { FuelType } from "../../../utils/model/FuelType"
import { Station } from "../../../utils/model/Station"
import GeolocationAcquireDialog from "./GeolocationAcquireDialog"
import ForbiddenParticipationDialog from "./ForbiddenParticipationDialog"
import GeolocationAvailableDialog from "./GeolocationAvailableDialog"
import GeolocationEnabledDialog from "./GeolocationEnabledDialog"
import PriceIncrementer from "./PriceIncrementer"
import { tcsBlueGray1, Theme } from "../../../mui/Theme"
import { ReactComponent as TrophyIcon } from '../../../icons/Icon_Pokal3.svg'

interface Props {
    station: Station,
    fuel: Fuel,
    fuelType: FuelType,
    onClose(): void,
    onUpdate(e: React.MouseEvent<HTMLElement>, coords: { latitude: number, longitude: number }, price: number): void
}

const UpdatePriceDialog = ({ station, fuel, fuelType, onClose, onUpdate }: Props) => {
    const { t } = useTranslation();

    const [price, setPrice] = useState<number>(displayPriceToNumber(fuel.displayPrice));
    const [hasPermission, setHasPermission] = useState(false);
    const { profile } = useProfile();

    const { coords, isGeolocationAvailable, isGeolocationEnabled } =
        useGeolocated({
            positionOptions: {
                enableHighAccuracy: false,
            },
            userDecisionTimeout: 5000,
        });

    const textOptions = {
        fuel: t(fuelType) ?? '{{fuel}}',
        station: station.displayName
    }

    useEffect(() => {
        setHasPermission(canParticipate(coords, profile, station));
    }, [setHasPermission, coords, profile, station]);

    const isPrivileged = isPrivilegedUser(profile);
    const effectiveCoords = coords ?? (isPrivileged
        ? {latitude: station.location.lat, longitude: station.location.lng} as GeolocationCoordinates
        : undefined);

    return (!isPrivileged && !isGeolocationAvailable) ?
        (<GeolocationAvailableDialog isGeolocationAvailable={isGeolocationAvailable} onClose={onClose} />) :
        (!isPrivileged && !isGeolocationEnabled) ?
            (<GeolocationEnabledDialog isGeolocationEnabled={isGeolocationEnabled} onClose={onClose} />) :
            effectiveCoords ? (
                <>
                    {hasPermission ?
                        (<Dialog open={true} onClose={onClose} className='dialog'>
                            <DialogTitle>
                                <Stack alignItems={'flex-start'}>
                                    <Typography variant="h3" sx={{ mb: 0.5 }}>{t('Update price')}</Typography>
                                    <Typography variant="body1" sx={{ mt: 0.25 }}>{t('PriceUpdate', textOptions)}</Typography>
                                </Stack>
                            </DialogTitle>
                            <DialogContent>
                                <Stack alignItems={'center'} justifyContent={'center'}
                                    sx={{ background: tcsBlueGray1, borderRadius: '8px', p: 1, pb: 2}}>
                                    <Typography fontWeight={'bold'} fontSize={14} color={Theme.palette.grey[700]}>{textOptions.fuel}</Typography>
                                    <Typography sx={{mb: 0.5}} variant="h3" fontSize={32} color="secondary">{t('CHF')}&nbsp;{displayPriceToFixed(price)}</Typography>
                                    <PriceIncrementer fuelType={fuelType} setPrice={setPrice}></PriceIncrementer>
                                </Stack>

                                <Stack direction={'row'} gap={1} sx={{p: 1, my: 1}}>
                                    <TrophyIcon className="customSVGIcon size48" />
                                    <Typography fontSize={11}>{t('PriceConfirmWin')}</Typography>
                                </Stack>
                            </DialogContent>
                            <DialogActions sx={{mt:1}}>
                                <Button variant="outlined" color="secondary" sx={{fontSize:14}} onClick={onClose}>{t('Cancel')}</Button>
                                <Button variant="contained" color="secondary" sx={{fontSize:14}} onClick={(e) => onUpdate(e, effectiveCoords!, price)}>{t('Confirm')}</Button>
                            </DialogActions>
                        </Dialog>) :
                        (<ForbiddenParticipationDialog hasPermission={hasPermission} onClose={onClose} />)
                    }
                </>
            ) : (<GeolocationAcquireDialog coords={effectiveCoords} onClose={onClose} />);
}

export default UpdatePriceDialog
